import { Image as ImageIcon, Upload, X } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from '@dukanti/shared';
import type { TranslationKey } from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface PromotionalAdMediaPickerProps {
  readonly file?: File | null;
  readonly value?: File | null;
  readonly existingUrl?: string | null;
  readonly errorKey?: TranslationKey | null;
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly onFileChange?: (file: File | null) => void;
  readonly onChange?: (file: File | null) => void;
}

export function PromotionalAdMediaPicker({
  file,
  value,
  existingUrl,
  errorKey,
  disabled = false,
  required = false,
  onFileChange,
  onChange,
}: PromotionalAdMediaPickerProps) {
  const { t, dir } = useTranslation();
  const inputId = useId();
  const selectedFile = file ?? value ?? null;
  const [localErrorKey, setLocalErrorKey] = useState<TranslationKey | null>(null);
  const previewUrl = useMemo(() => {
    if (!selectedFile || typeof URL.createObjectURL !== 'function') return null;
    return URL.createObjectURL(selectedFile);
  }, [selectedFile]);
  const visibleUrl = previewUrl ?? existingUrl ?? null;
  const visibleErrorKey = errorKey ?? localErrorKey;

  useEffect(() => {
    return () => {
      if (previewUrl && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  function handleChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const next = event.currentTarget.files?.[0] ?? null;
    if (next && !['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(next.type)) {
      setLocalErrorKey('admin.promotionalAds.errors.mediaInvalidType' as TranslationKey);
      event.currentTarget.value = '';
      return;
    }
    setLocalErrorKey(null);
    onFileChange?.(next);
    onChange?.(next);
    event.currentTarget.value = '';
  }

  return (
    <div dir={dir} data-testid="promotional-ad-media-picker" className="space-y-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div
          className={cn(
            'border-border bg-admin-surface-muted flex aspect-[3/1] w-full min-w-0 max-w-md items-center justify-center overflow-hidden rounded-lg border',
            visibleErrorKey && 'border-destructive',
          )}
        >
          {visibleUrl ? (
            <img
              src={visibleUrl}
              alt={t('admin.promotionalAds.form.mediaLabel' as TranslationKey)}
              className="h-full w-full object-cover"
            />
          ) : (
            <ImageIcon className="text-muted-foreground size-9" strokeWidth={1.5} aria-hidden />
          )}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Button type="button" variant="outline" disabled={disabled} asChild>
            <label htmlFor={inputId} className="cursor-pointer">
              <Upload className="size-4" aria-hidden />
              <span>{t('admin.promotionalAds.form.mediaLabel' as TranslationKey)}</span>
            </label>
          </Button>
          {selectedFile ? (
            <Button
              type="button"
              variant="ghost"
              disabled={disabled}
              onClick={() => {
                onFileChange?.(null);
                onChange?.(null);
              }}
            >
              <X className="size-4" aria-hidden />
              <span>{t('common.cancel')}</span>
            </Button>
          ) : null}
        </div>
      </div>
      <input
        id={inputId}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="sr-only"
        disabled={disabled}
        required={required}
        onChange={handleChange}
        data-testid="promotional-ad-media-input"
        aria-describedby={`${inputId}-hint${visibleErrorKey ? ` ${inputId}-error` : ''}`}
      />
      <p id={`${inputId}-hint`} className="text-muted-foreground text-start text-xs">
        {t('admin.promotionalAds.form.mediaHint' as TranslationKey)}
      </p>
      {visibleErrorKey ? (
        <p id={`${inputId}-error`} role="alert" className="text-destructive text-start text-xs">
          {t(visibleErrorKey)}
        </p>
      ) : null}
    </div>
  );
}
